/**
 * Rank slots in words (19/10/2026): where a group stands — how many of its
 * drops are tied to #1…#N — and what the button will do, exactly.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DraftSlot } from '../../../../drop-generator.types';
import type { RankSlotsPlan } from './rank-slots';

const drops = (n: number) => `${n} drop${n === 1 ? '' : 's'}`;
const ranks = (from: number, to: number) => (from === to ? `#${from}` : `#${from}–#${to}`);

/** The button: create, tie and add, tie, or tie and go round again. */
export function tieWords(plan: RankSlotsPlan, n: number): string {
  if (n < 1) return 'No list to rank today';
  if (plan.use === 0) return `Create ${drops(n)} — ${ranks(1, n)}`;
  if (plan.create > 0) return `Tie my ${drops(plan.use)} to ${ranks(1, plan.use)}, add ${ranks(plan.use + 1, n)}`;
  return plan.use > n ? `Tie my ${drops(plan.use)} to ${ranks(1, n)}, then round again` : `Tie my ${drops(plan.use)} to ${ranks(1, n)}`;
}

/** Where the group stands: complete when every rank 1..n has a switched-on drop. */
export function rankState(slots: DraftSlot[], groupId: string, n: number): { complete: boolean; words: string } {
  const mine = slots.filter((s) => s.enabled && s.listGroupIds.includes(groupId));
  const tied = mine.filter((s) => s.listsRankByGroup?.[groupId] !== undefined);
  const covered = new Set(tied.map((s) => s.listsRankByGroup![groupId]));
  const complete = n > 0 && Array.from({ length: n }, (_, i) => i + 1).every((k) => covered.has(k));
  if (complete) return { complete, words: `${ranks(1, n)} each have their own drop` };
  if (tied.length === 0) return { complete, words: mine.length ? `none of its ${drops(mine.length)} is tied yet` : 'no drop yet' };
  const missing = Array.from({ length: n }, (_, i) => i + 1).filter((k) => !covered.has(k));
  const gap = missing.length === missing[missing.length - 1] - missing[0] + 1 ? ranks(missing[0], missing[missing.length - 1]) : `${missing.length} ranks`;
  return { complete, words: `${tied.length} of ${drops(mine.length)} tied · ${gap} ${missing.length === 1 ? 'has' : 'have'} no drop yet` };
}
